import { last } from "@shared/common/types/Array";
import { Point } from "../Point";

export class PointHelper {
  /**
   * Reverses the points if the text is upside down. This is done to make sure the text is always readable.
   * The first and last point is used to calculate the rotation of the text.
   * @param points The points.
   * @returns
   */
  public static rotateIfUpsideDown(points: Point[]) {
    const rotation = PointHelper.getRotation(points[0], last(points));
    if (rotation >= 90 || rotation <= -90) {
      return points.toReversed();
    }
    return points;
  }

  public static calculateDistancesBetweenPoints(points: Point[]) {
    let totalDistance = 0;
    const distances = points.reduce<number[]>((acc, point, index) => {
      if (index === 0) {
        return [0];
      }
      const distance = PointHelper.getPointsDistance(points[index - 1], point);
      totalDistance += distance;
      return [...acc, last(acc) + distance];
    }, []);

    return { distances, totalDistance };
  }

  /**
   * Returns a point that is the given distance away from the given point in the given direction.
   * @param distance The distance from the point.
   * @param angle The angle in degrees.
   * @returns
   * @example
   * const point = PointHelper.getPointInDirection(10, 45);
   * // point is [7.0710678118654755, 7.0710678118654755]
   * const point = PointHelper.getPointInDirection(10, 90);
   * // point is [0, 10]
   */
  public static getPointInDirection(distance: number, angle: number) {
    const angleRad = (angle * Math.PI) / 180;
    const x = Math.cos(angleRad) * distance;
    const y = Math.sin(angleRad) * distance;
    return [x, y];
  }

  /**
   * Returns the distance between two points.
   * @param p1 The first point.
   * @param p2 The second point.
   * @returns
   * @example
   * const distance = PointHelper.getDistance([0, 0], [3, 4]);
   * // distance is 5
   * const distance = PointHelper.getDistance([0, 0], [0, 0]);
   * // distance is 0
   */
  public static getDistance(p1: Point, p2: Point) {
    const x = p2.x - p1.x;
    const y = p2.y - p1.y;
    return Math.sqrt(x * x + y * y);
  }

  static toDataString(points: Point[]): string {
    return points.reduce<string>((p, c) => `${p} ${c.x},${c.y}`, "M");
    //return path.points.map((i, x) => `${x === 0 ? "M" : "L"} ${i.x},${i.y}`).join(" ");
  }

  public static equals(p1: Point, p2: Point) {
    return p1.x === p2.x && p1.y === p2.y;
  }

  public static fromNumberArray(array: number[]): Point[] {
    return array.reduce<Point[]>((prev, _, index, arr) => {
      if (index % 2 === 0) {
        return [...prev, { x: arr[index], y: arr[index + 1] } as Point];
      }
      return prev;
    }, []);
  }

  public static toNumberArray(points: Point[]): number[] {
    return points.reduce<number[]>((prev, curr) => [...prev, curr.x, curr.y], []);
  }

  /**
   * Returns the distance between the given points. The distance is the sum of the distances between
   * each point.
   * @param points The points.
   * @returns
   * @example
   * const distance = PointHelper.getPointsDistance([0, 0], [3, 4], [6, 8]);
   * // distance is 10
   * const distance = PointHelper.getPointsDistance([0, 0], [0, 0]);
   * // distance is 0
   * const distance = PointHelper.getPointsDistance([0, 0], [3, 4]);
   * // distance is 5
   */
  public static getPointsDistance(...points: Point[]) {
    return points.reduce(
      (prev, curr, index, array) =>
        prev +
        (index >= array.length - 1
          ? 0
          : PointHelper.getDistanceBetweenTwoPoints(curr, array[index + 1])),
      0,
    );
  }

  /**
   * Calculates the point between two points. The length is the distance from the start of the line.
   * @param start The start point of the line.
   * @param end The end point of the line.
   * @param distanceFromStart The distance from the start of the line.
   * @returns
   */
  public static getPointBetweenPoints(start: Point, end: Point, distanceFromStart: number): Point {
    const distance = PointHelper.getPointsDistance(start, end);
    const ratio = distanceFromStart / distance;
    return new Point(start.x + (end.x - start.x) * ratio, start.y + (end.y - start.y) * ratio);
  }

  public static getDistanceBetweenTwoPoints(p1: Point, p2: Point) {
    const x = p2.x - p1.x;
    const y = p2.y - p1.y;
    return Math.sqrt(x * x + y * y);
  }

  /**
   * Returns the angle between two points in degrees. The angle is calculated from the x-axis.
   * @param point1 The first point.
   * @param point2 The second point.
   * @returns
   * @example
   * const angle = PointHelper.getRotation({ x: 0, y: 0 }, { x: 1, y: 1 });
   * // angle is 45
   * const angle = PointHelper.getRotation({ x: 0, y: 0 }, { x: 1, y: 0 });
   * // angle is 0
   */
  public static getRotation(point1: Point, point2: Point) {
    const point1x = point1.x;
    const point1y = point1.y;
    const point2x = point2.x;
    const point2y = point2.y;

    const angleDeg = (Math.atan2(point2y - point1y, point2x - point1x) * 180) / Math.PI;

    return angleDeg % 360;
  }
}
